import { useEffect, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';

import type { SheetOpportunity } from '../../../hooks/useOpportunitySheet';
import { editPatch, formOf, type OpportunityForm, type ProductMode } from '../../../lib/crm/opportunityEdit';
import { REVENUE_TYPE_LABEL } from '../../../lib/crm/opportunitySheet';
import { sensitive } from '../../../lib/presentationMode';
import { useCRMStore } from '../../../store/crmStore';
import type { CRMOpportunity } from '../../../types/crm';
import { getIntervalLabel, getMonthlyAmount } from '../../../utils/pricing';
import { BTN_GHOST, BTN_PRIMARY } from '../../bureau/buttons';
import { FIELD, SEG, SEG_ITEM } from '../../bureau/encre';
import { SheetBlock } from './EncreSheet';
import { SheetFormFooter } from './SheetFormFooter';

const LABEL = 'mb-1.5 block text-xs font-medium text-desk-soft';

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className={LABEL}>{label}</span>
      {children}
    </label>
  );
}

const PRODUCT_MODES: [ProductMode, string][] = [
  ['none', 'Aucun'],
  ['catalogue', 'Catalogue'],
  ['custom', 'Personnalisé'],
];

/**
 * « Modifier » a deal in its sheet (PLAN-ENCRE-OUTILS O3): its name and figures, the dates it should
 * close, its revenue and product (the catalogue's — MRR and ARR derived by the database — or one
 * typed by hand with its MRR), why it was lost. « Enregistrer » writes only what changed and stays
 * open on a refusal; the stage moves from the sheet itself, and closing has its own gestures.
 */
export function OpportunitySheetEdit({
  deal,
  onSaved,
  onDone,
  onDirty,
}: {
  deal: SheetOpportunity;
  onSaved: () => Promise<void>;
  onDone: () => void;
  /** Whether a typed change is not saved (the sheet then asks before closing). */
  onDirty: (dirty: boolean) => void;
}) {
  const { updateOpportunity, stripeProducts, fetchStripeProducts, calculateRevenue } = useCRMStore(
    useShallow((s) => ({
      updateOpportunity: s.updateOpportunity,
      stripeProducts: s.stripeProducts,
      fetchStripeProducts: s.fetchStripeProducts,
      calculateRevenue: s.calculateRevenue,
    })),
  );
  // The deal's own product stays choosable when the catalogue no longer offers it (archived).
  const ownProduct = deal.product && !stripeProducts.some((p) => p.id === deal.product?.id) ? deal.product : null;
  const [initial] = useState<OpportunityForm>(() => formOf(deal));
  const [form, setForm] = useState<OpportunityForm>(initial);
  const changed = (Object.keys(initial) as (keyof OpportunityForm)[]).some((k) => form[k] !== initial[k]);
  useEffect(() => onDirty(changed), [changed, onDirty]);
  useEffect(() => () => onDirty(false), [onDirty]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof OpportunityForm>(key: K, value: OpportunityForm[K]) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (stripeProducts.length === 0) void fetchStripeProducts();
  }, [stripeProducts.length, fetchStripeProducts]);

  const save = async () => {
    const result = editPatch(deal, form);
    if ('error' in result) return setError(result.error);
    if (Object.keys(result.patch).length === 0) return onDone();
    setSaving(true);
    const failed = await updateOpportunity(deal.id, result.patch as Partial<CRMOpportunity>);
    setSaving(false);
    setError(failed);
    if (failed) return;
    void calculateRevenue();
    await onSaved();
    onDone();
  };

  return (
    <div>
      <SheetBlock title="L'affaire">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Nom">
              <input className={FIELD} value={form.name} onChange={(e) => set('name', e.target.value)} />
            </Field>
          </div>
          <Field label="Montant (€)">
            <input className={FIELD} inputMode="decimal" value={form.amount} placeholder="12 000" onChange={(e) => set('amount', e.target.value)} />
          </Field>
          <Field label="Probabilité (%)">
            <input className={FIELD} inputMode="numeric" value={form.probability} onChange={(e) => set('probability', e.target.value)} />
          </Field>
          <Field label="Clôture visée">
            <input type="date" className={FIELD} value={form.target_close_date} onChange={(e) => set('target_close_date', e.target.value)} />
          </Field>
          {deal.status !== 'open' && (
            <Field label="Clôturée le">
              <input type="date" className={FIELD} value={form.close_date} onChange={(e) => set('close_date', e.target.value)} />
            </Field>
          )}
          {deal.status === 'lost' && (
            <div className="sm:col-span-2">
              <Field label="Raison de la perte">
                <input className={FIELD} value={form.reason_lost} onChange={(e) => set('reason_lost', e.target.value)} />
              </Field>
            </div>
          )}
        </div>
      </SheetBlock>

      <SheetBlock title="Revenu">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Type de revenu">
            <select className={FIELD} value={form.revenue_type} onChange={(e) => set('revenue_type', e.target.value as CRMOpportunity['revenue_type'])}>
              {(Object.keys(REVENUE_TYPE_LABEL) as CRMOpportunity['revenue_type'][]).map((t) => (
                <option key={t} value={t}>
                  {REVENUE_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </Field>
          {form.revenue_type !== 'recurring' && (
            <Field label="Montant ponctuel (€)">
              <input className={FIELD} inputMode="decimal" value={form.one_time_amount} onChange={(e) => set('one_time_amount', e.target.value)} />
            </Field>
          )}
        </div>
        <p className={`${LABEL} mt-4`} id={`deal-product-${deal.id}`}>
          Produit
        </p>
        <div role="group" aria-labelledby={`deal-product-${deal.id}`} className={SEG}>
          {PRODUCT_MODES.map(([mode, label]) => (
            <button key={mode} type="button" aria-pressed={form.product === mode} onClick={() => set('product', mode)} className={SEG_ITEM}>
              {label}
            </button>
          ))}
        </div>
        {form.product === 'catalogue' && (
          <div className="mt-3">
            <Field label="Produit du catalogue">
              <select className={FIELD} value={form.stripe_product_id} onChange={(e) => set('stripe_product_id', e.target.value)}>
                <option value="">Choisir…</option>
                {ownProduct && <option value={ownProduct.id}>{ownProduct.name} (plus au catalogue)</option>}
                {stripeProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {sensitive(getMonthlyAmount(p).toLocaleString('fr-FR', { maximumFractionDigits: 0 }))} €/mois
                    {p.interval === 'year' || p.interval_count > 1 ? ` (facturé par ${getIntervalLabel(p)})` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <p className="mt-1.5 text-xs text-desk-muted">Le MRR et l’ARR suivent le prix du produit.</p>
          </div>
        )}
        {form.product === 'custom' && (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="Nom du produit">
              <input className={FIELD} value={form.custom_product_name} onChange={(e) => set('custom_product_name', e.target.value)} />
            </Field>
            <Field label="MRR (€/mois)">
              <input className={FIELD} inputMode="decimal" value={form.custom_mrr} onChange={(e) => set('custom_mrr', e.target.value)} />
            </Field>
          </div>
        )}
      </SheetBlock>

      <SheetFormFooter error={error}>
        <button type="button" onClick={onDone} disabled={saving} className={BTN_GHOST}>
          Annuler
        </button>
        <button type="button" onClick={() => void save()} disabled={saving} className={BTN_PRIMARY}>
          {saving && <Loader2 size={15} className="animate-spin" aria-hidden="true" />}
          Enregistrer
        </button>
      </SheetFormFooter>
    </div>
  );
}
